import React, { useEffect, useState } from "react"
import clsx from "classnames"
import PropTypes from "prop-types"
import i18n from "lib/i18n"
import { Dropdown } from "../common/Dropdown"
import watchWindowWidth from "lib/watchWindowWidth"

import { ConfigProps } from "components/PropTypes/ConfigProps"
import HeroImage from "./HeroImage"

// Shared text styles for hero title, subtitle, and transaction ID (all white Montserrat
// above the gradient overlay). Formerly :local .title/.donatingTo/.transactionId in styles.scss.
const baseTextClasses = "relative z-10 text-white font-sans-heading leading-tight"

const HeroComponent = ({ config = {}, regions = [], transactionId = null }) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)

  useEffect(() => watchWindowWidth(setWindowWidth), [])

  const isDesktop = windowWidth > 600

  const onRegionSelect = (event) => {
    window.location = `/${event.target.value}`
  }

  // The hero text container: desktop is a centred 972px column with generous padding;
  // mobile is full-width with 32px×24px padding (38px top with a transaction ID).
  const containerClasses = clsx(
    "relative mx-auto box-border",
    isDesktop && "max-w-243",
    isDesktop
      ? transactionId
        ? "pt-10 pb-14"
        : "pt-16 pb-14"
      : clsx("h-full px-6 py-8", transactionId && "pt-10"),
  )

  // Hero title: 52px on desktop, 34px on mobile. With a transaction ID on desktop,
  // the title is pushed down 70px below the transaction ID line.
  const titleClasses = clsx(
    baseTextClasses,
    "mb-4 font-bold whitespace-pre-line",
    isDesktop ? "text-6xl" : "text-4xl",
    isDesktop && transactionId && "pt-18",
  )

  let containerCopy
  if (transactionId) {
    containerCopy = (
      <div className={containerClasses}>
        <h6 className={clsx(baseTextClasses, "text-base font-medium", !isDesktop && "pb-5")}>
          {i18n.t("hero_component.label.transaction_number")} {transactionId}
        </h6>
        <h1
          className={titleClasses}
          dangerouslySetInnerHTML={{
            __html: i18n.t("hero_component.label.thank_you_for_donating"),
          }}
        />
      </div>
    )
  } else {
    const regionOptions = regions.map((region) => (
      <option key={region.code} value={region.code}>
        {region.name}
      </option>
    ))

    containerCopy = (
      <div className={containerClasses}>
        <h1 className={titleClasses}>{i18n.t("hero_component.label.title")}</h1>
        <h2 className={clsx(baseTextClasses, "mb-3 text-xl font-medium")}>
          {i18n.t("hero_component.label.you_are_donating_to")}
        </h2>
        <div className="relative z-3 w-fit">
          <Dropdown
            value={config.region_code}
            onChange={onRegionSelect}
            name="regionSelector"
            className="focus:ring-2 focus:ring-blue-600 focus:outline-hidden"
            label={i18n.t("hero_component.label.region_selector")}
          >
            {regionOptions}
          </Dropdown>
        </div>
      </div>
    )
  }

  return (
    <div className="relative after:absolute after:inset-0 after:z-1 after:bg-gradient-to-b after:from-black/20 after:to-black/50 after:content-['']">
      <HeroImage />
      {containerCopy}
    </div>
  )
}

HeroComponent.propTypes = {
  config: ConfigProps,
  regions: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string,
      name: PropTypes.string,
    }),
  ),
  transactionId: PropTypes.number,
}

export default HeroComponent
